import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BellRing,
  CheckCheck,
  ChevronRight,
  Package,
  RefreshCw,
} from "lucide-react";
import { toast } from "react-toastify";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  getStoredUserId,
  getStoredUserName,
} from "../utils/sessionUser";
import {
  cacheWebsiteNotifications,
  emitWebsiteNotificationsUpdated,
  fetchWebsiteNotifications,
  getCachedWebsiteNotifications,
  markAllWebsiteNotificationsRead,
} from "../utils/websiteNotificationsApi";

const formatNotificationTime = (value) => {
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(parsedDate);
};

const formatDateLabel = (value) => {
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return "Recent";
  }

  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const sameDay = (first, second) =>
    first.getFullYear() === second.getFullYear() &&
    first.getMonth() === second.getMonth() &&
    first.getDate() === second.getDate();

  if (sameDay(parsedDate, today)) {
    return "Today";
  }

  if (sameDay(parsedDate, yesterday)) {
    return "Yesterday";
  }

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(parsedDate);
};

const groupNotificationsByDate = (notifications) =>
  notifications.reduce((groups, notification) => {
    const label = formatDateLabel(notification?.timestamp);
    if (!groups[label]) {
      groups[label] = [];
    }
    groups[label].push(notification);
    return groups;
  }, {});

const Notifications = () => {
  const navigate = useNavigate();
  const cachedNotifications = getCachedWebsiteNotifications({
    token: localStorage.getItem("token"),
    userId: getStoredUserId(),
  });
  const [loading, setLoading] = useState(!cachedNotifications);
  const [notifications, setNotifications] = useState(
    () => cachedNotifications || []
  );
  const [syncingReadState, setSyncingReadState] = useState(false);

  const loadNotifications = useCallback(
    async ({ markRead = false, force = false } = {}) => {
      const token = localStorage.getItem("token");
      const userId = getStoredUserId();

      if (!token || !userId) {
        setLoading(false);
        navigate("/");
        return;
      }

      try {
        if (!markRead) {
          setLoading(true);
        }

        const fetchedNotifications = await fetchWebsiteNotifications({
          token,
          userId,
          force,
        });

        setNotifications(fetchedNotifications);
        const unreadCount = fetchedNotifications.filter(
          (notification) => !notification?.isRead
        ).length;
        emitWebsiteNotificationsUpdated({ unreadCount });

        if (markRead && unreadCount > 0) {
          // Show the fetched data immediately. Marking records as read happens
          // in the background and must not hold up the notifications page.
          const readNotifications = fetchedNotifications.map((notification) =>
              notification?.isRead
                ? notification
                : { ...notification, isRead: true }
          );
          setNotifications(readNotifications);
          cacheWebsiteNotifications({
            token,
            userId,
            notifications: readNotifications,
          });
          emitWebsiteNotificationsUpdated({ unreadCount: 0 });
          setSyncingReadState(true);

          void markAllWebsiteNotificationsRead({
            token,
            notifications: fetchedNotifications,
          })
            .catch((error) => {
              toast.error(error.message || "Unable to mark notifications as read.");
            })
            .finally(() => {
              setSyncingReadState(false);
            });
        }
      } catch (error) {
        toast.error(error.message || "Unable to load notifications.");
      } finally {
        setLoading(false);
        if (!markRead) {
          setSyncingReadState(false);
        }
      }
    },
    [navigate]
  );

  useEffect(() => {
    loadNotifications({ markRead: true });
  }, [loadNotifications]);

  const groupedNotifications = useMemo(
    () => groupNotificationsByDate(notifications),
    [notifications]
  );

  return (
    <section className="min-h-screen w-screen overflow-x-hidden bg-[linear-gradient(180deg,#fdf8ee_0%,#fffdf8_22%,#fff9ef_100%)] text-[#1d2433]">
      <div className="flex min-h-screen w-full flex-col px-0 pb-3 pt-2 sm:pt-3">
        <Navbar />

        <div className="mt-2 w-full overflow-hidden rounded-[24px] bg-[linear-gradient(135deg,#fffefb_0%,#fffdf8_34%,#fff7ea_70%,#fffef9_100%)] shadow-[0_18px_50px_rgba(177,146,90,0.12)]">
          <div className="px-3 pb-4 pt-3 sm:px-5 lg:px-8">
            <div className="rounded-[20px] border border-[#eee3d1] bg-white/92 px-3 py-3 shadow-[0_14px_36px_rgba(191,166,116,0.10)] sm:px-5 sm:py-5 lg:rounded-[24px] lg:px-6 lg:py-6">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <h1 className="text-[24px] font-extrabold tracking-[-0.04em] text-[#1f2432] sm:text-[34px] lg:text-[40px]">
                    Notifications
                  </h1>
                  <p className="mt-1.5 text-[13px] text-[#7a8497] sm:text-[15px] lg:text-[16px]">
                    All updates for {getStoredUserName()} from your website and app activity
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => loadNotifications({ force: true })}
                  className="inline-flex h-[42px] items-center justify-center gap-2 rounded-[12px] border border-[#f0dcae] bg-[#fff9eb] px-4 text-[14px] font-semibold text-[#7c5b16] transition hover:bg-[#fff3d2] sm:h-[48px] sm:px-5"
                >
                  <RefreshCw className="h-4 w-4" />
                  Refresh
                </button>
              </div>

              <div className="mt-5 rounded-[18px] border border-[#ebe3d6] bg-[#fffefb] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.9)] sm:p-5">
                <div className="flex flex-col gap-3 rounded-[18px] border border-[#f0e0b8] bg-[linear-gradient(135deg,#fffdf7_0%,#fff6df_100%)] p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-[#f1a208] shadow-sm">
                      <BellRing className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#1f2432]">
                        {notifications.length} total notifications
                      </p>
                      <p className="text-xs text-[#7a8497]">
                        Opening this page marks all unread notifications as read
                      </p>
                    </div>
                  </div>

                  {syncingReadState ? (
                    <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-2 text-xs font-semibold text-[#6b7280] shadow-sm">
                      <CheckCheck className="h-4 w-4" />
                      Marking as read...
                    </div>
                  ) : null}
                </div>

                {loading ? (
                  <div className="flex min-h-[280px] items-center justify-center">
                    <div className="h-12 w-12 animate-spin rounded-full border-[4px] border-[#f4c75b] border-t-[#f59e0b]" />
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="flex min-h-[280px] flex-col items-center justify-center rounded-[18px] border border-dashed border-[#eadfcb] bg-[#fffdfa] px-4 text-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#fff4dd] text-[#f5a10a]">
                      <BellRing className="h-7 w-7" />
                    </div>
                    <p className="mt-4 text-[20px] font-semibold text-[#1f2432]">
                      No notifications yet
                    </p>
                    <p className="mt-2 max-w-[360px] text-[14px] leading-6 text-[#697386]">
                      Order updates, payment alerts and delivery progress will appear here.
                    </p>
                  </div>
                ) : (
                  <div className="mt-5 space-y-5">
                    {Object.entries(groupedNotifications).map(([label, items]) => (
                      <div key={label}>
                        <div className="mb-3 flex items-center gap-2">
                          <h2 className="text-sm font-bold uppercase tracking-[0.18em] text-[#7a8497]">
                            {label}
                          </h2>
                          <span className="rounded-full bg-[#f4f5f8] px-2.5 py-1 text-[11px] font-semibold text-[#6b7280]">
                            {items.length}
                          </span>
                        </div>

                        <div className="space-y-3">
                          {items.map((notification) => (
                            <div
                              key={notification._id}
                              className={`rounded-[18px] border px-4 py-4 shadow-sm transition ${
                                notification?.isRead
                                  ? "border-[#ece5d9] bg-white"
                                  : "border-[#f3d178] bg-[linear-gradient(135deg,#fffdf5_0%,#fff8e7_100%)]"
                              }`}
                            >
                              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                <div className="flex min-w-0 gap-3">
                                  <div className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#fff4dd] text-[#f5a10a]">
                                    <Package className="h-5 w-5" />
                                  </div>

                                  <div className="min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <h3 className="text-[16px] font-bold text-[#1f2432]">
                                        {notification?.title || "Notification"}
                                      </h3>
                                      {!notification?.isRead ? (
                                        <span className="rounded-full bg-[#0f9d58]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-[#0f9d58]">
                                          New
                                        </span>
                                      ) : null}
                                    </div>

                                    <p className="mt-1 text-[14px] leading-6 text-[#5e697d]">
                                      {notification?.body || "You have a new update."}
                                    </p>

                                    <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-[#7a8497]">
                                      <span className="rounded-full bg-[#f7f7f8] px-2.5 py-1 font-semibold uppercase tracking-[0.08em]">
                                        {String(notification?.type || "custom").replaceAll("_", " ")}
                                      </span>
                                      {notification?.orderId ? (
                                        <span className="rounded-full bg-[#eef5ff] px-2.5 py-1 font-semibold text-[#2f5da8]">
                                          Order #{notification.orderId}
                                        </span>
                                      ) : null}
                                    </div>
                                  </div>
                                </div>

                                <div className="flex shrink-0 items-center gap-3 sm:pl-4">
                                  <span className="text-right text-xs font-medium text-[#7a8497]">
                                    {formatNotificationTime(notification?.timestamp)}
                                  </span>
                                  {notification?.orderId ? (
                                    <button
                                      type="button"
                                      onClick={() => navigate("/orders")}
                                      className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#eadfcb] bg-white text-[#6b7280] transition hover:border-[#f1b400] hover:text-[#1f2432]"
                                      aria-label="Open orders"
                                    >
                                      <ChevronRight className="h-4 w-4" />
                                    </button>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <Footer className="pt-0" />
        </div>
      </div>
    </section>
  );
};

export default Notifications;
